import { useQuery } from "@tanstack/react-query";
import { MuiTable, TableCell } from "src/components/MuiTable/MuiTable.tsx";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { formatDateTime } from "@exonn/utils";
import type {
  SupplierOrderStatusHistoryResource,
  SupplierOrderTransmissionResource,
} from "@exonn/js-sdk";
import { Panel } from "src/components/Panel/Panel.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import { useResourceFormData } from "src/components/Form/hooks/useResourceFormData.ts";
import { useResourceFormTranslation } from "src/components/Form/hooks/useResourceFormTranslation.ts";
import type { SupplierOrderFormData } from "src/admin/modules/supplier-order/forms/supplier-order/config.tsx";

// История статусов (кто, когда, причина) и журнал передач поставщику.
export const History = () => {
  const { t } = useResourceFormTranslation();
  const data = useResourceFormData<SupplierOrderFormData>();
  const order = data?.data;
  const exonnApi = useExonnApi();

  const history = useQuery({
    queryKey: [
      ResourceEnum.SupplierOrder,
      order?.id,
      order?.updated_at,
      "status-history",
    ],
    enabled: !!order?.id,
    queryFn: async () =>
      (
        await exonnApi.client.get<{
          data: SupplierOrderStatusHistoryResource[];
        }>(`/supplier-order/${order?.id}/status-history`)
      ).data.data,
  });
  const transmissions = useQuery({
    queryKey: [
      ResourceEnum.SupplierOrder,
      order?.id,
      order?.updated_at,
      "transmissions",
    ],
    enabled: !!order?.id,
    queryFn: async () =>
      (
        await exonnApi.client.get<{
          data: SupplierOrderTransmissionResource[];
        }>(`/supplier-order/${order?.id}/transmissions`)
      ).data.data,
  });

  if (!order?.id) {
    return null;
  }

  return (
    <div
      style={{ padding: 15, display: "flex", flexDirection: "column", gap: 15 }}
    >
      <Panel>
        <Panel.Header>
          <Panel.Title>
            <Panel.Icon>
              <span className={"icon-15-basic_info"} />
            </Panel.Icon>
            {t("history.title")}
          </Panel.Title>
        </Panel.Header>
        <Panel.Content>
          {(history.data ?? []).length === 0 ? (
            <TextV2 variant={"body1"}>{t("history.empty")}</TextV2>
          ) : (
            <MuiTable size={"small"}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("history.date")}</TableCell>
                  <TableCell>{t("history.from")}</TableCell>
                  <TableCell>{t("history.to")}</TableCell>
                  <TableCell>{t("history.reason")}</TableCell>
                  <TableCell>{t("history.user")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(history.data ?? []).map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{formatDateTime(entry.created_at)}</TableCell>
                    <TableCell>
                      {entry.from_status
                        ? t(`status.${entry.from_status}`)
                        : "—"}
                    </TableCell>
                    <TableCell>{t(`status.${entry.to_status}`)}</TableCell>
                    <TableCell>{entry.reason ?? ""}</TableCell>
                    <TableCell>{entry.user?.name ?? ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          )}
        </Panel.Content>
      </Panel>
      <Panel>
        <Panel.Header>
          <Panel.Title>
            <Panel.Icon>
              <span className={"icon-15-basic_info"} />
            </Panel.Icon>
            {t("transmissions.title")}
          </Panel.Title>
        </Panel.Header>
        <Panel.Content>
          {(transmissions.data ?? []).length === 0 ? (
            <TextV2 variant={"body1"}>{t("transmissions.empty")}</TextV2>
          ) : (
            <MuiTable size={"small"}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("history.date")}</TableCell>
                  <TableCell>{t("transmissions.attempt")}</TableCell>
                  <TableCell>{t("transmissions.channel")}</TableCell>
                  <TableCell>{t("transmissions.status")}</TableCell>
                  <TableCell>{t("transmissions.result")}</TableCell>
                  <TableCell>{t("history.user")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(transmissions.data ?? []).map((transmission) => (
                  <TableRow key={transmission.id}>
                    <TableCell>
                      {formatDateTime(transmission.created_at)}
                    </TableCell>
                    <TableCell>{transmission.attempt}</TableCell>
                    <TableCell>
                      {t(`transmission-channel.${transmission.channel}`)}
                    </TableCell>
                    <TableCell>
                      {t(`transmission-status.${transmission.status}`)}
                    </TableCell>
                    <TableCell>
                      {transmission.error ?? transmission.response ?? ""}
                    </TableCell>
                    <TableCell>{transmission.user?.name ?? ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          )}
        </Panel.Content>
      </Panel>
    </div>
  );
};
